import { useEffect, useState } from "react";
import { Loader2, Building2, GraduationCap, Users, FileText, Wallet, BadgeCheck, Activity, ShieldCheck, Plus, Check, X, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BulkImportDialog } from "@/components/BulkImportDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const COLORS = ["#B8860B", "#E0B34A", "#C8992E", "#8B6914", "#D4AF37", "#A67C00", "#CDA434", "#9C7A3C"];

const EMPTY_INST = { name: "", city: "", state: "", description: "", accreditation: "", website: "", established: "", logo_url: "", cover_url: "https://images.pexels.com/photos/33977829/pexels-photo-33977829.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", verified: false };
const EMPTY_COURSE = { institute_id: "", title: "", specialty: "", city: "", state: "", format: "Offline", duration: "", fee: "", application_fee: "500", seats: "20", stipend: "", eligibility: "", tags: "", description: "", image_url: "https://images.unsplash.com/photo-1550831107-1553da8c8464?crop=entropy&cs=srgb&fm=jpg&q=85&w=940", application_deadline: "" };

export default function AdminCommandCenter() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [claims, setClaims] = useState([]);
  const [instOpen, setInstOpen] = useState(false);
  const [courseOpen, setCourseOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [instForm, setInstForm] = useState(EMPTY_INST);
  const [courseForm, setCourseForm] = useState(EMPTY_COURSE);

  const load = async () => {
    const [s, a, i, c] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/activity"), api.get("/admin/institutes"), api.get("/admin/claims"),
    ]);
    setStats(s.data); setActivity(a.data); setInstitutes(i.data); setClaims(c.data);
  };
  useEffect(() => { load(); }, []);

  const verify = async (id) => {
    try { await api.post(`/institutes/${id}/verify`); toast.success("Institute verified"); load(); }
    catch { toast.error("Failed to verify"); }
  };

  const setI = (k, v) => setInstForm((f) => ({ ...f, [k]: v }));
  const setC = (k, v) => setCourseForm((f) => ({ ...f, [k]: v }));

  const saveInstitute = async () => {
    setSaving(true);
    try {
      await api.post("/institutes", { ...instForm });
      toast.success("Institute added — the institute can now claim it");
      setInstOpen(false); setInstForm(EMPTY_INST); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add"); }
    finally { setSaving(false); }
  };

  const saveCourse = async () => {
    if (!courseForm.institute_id) { toast.error("Select an institute first"); return; }
    setSaving(true);
    try {
      await api.post("/courses", {
        ...courseForm,
        fee: parseFloat(courseForm.fee) || 0,
        application_fee: parseFloat(courseForm.application_fee) || 500,
        seats: parseInt(courseForm.seats) || 20,
        eligibility: courseForm.eligibility.split(",").map((s) => s.trim()).filter(Boolean),
        tags: courseForm.tags.split(",").map((s) => s.trim()).filter(Boolean),
        curriculum: [], documents_required: [],
        admission_process: ["Submit online application", "Document verification", "Interview", "Admission & payment"],
      });
      toast.success("Course published on behalf of the institute");
      setCourseOpen(false); setCourseForm(EMPTY_COURSE); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
    finally { setSaving(false); }
  };

  const approveClaim = async (id) => { try { await api.post(`/admin/claims/${id}/approve`); toast.success("Claim approved"); load(); } catch { toast.error("Failed to approve"); } };
  const rejectClaim = async (id) => { try { await api.post(`/admin/claims/${id}/reject`); toast.success("Claim rejected"); load(); } catch { toast.error("Failed to reject"); } };

  if (!stats) return <div><Navbar /><div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div>;

  const cards = [
    { icon: Building2, label: "Institutes", value: stats.total_institutes, sub: `${stats.verified_institutes} verified` },
    { icon: GraduationCap, label: "Courses", value: stats.total_courses },
    { icon: Users, label: "Professionals", value: stats.total_professionals },
    { icon: FileText, label: "Applications", value: stats.total_applications, sub: `${stats.accepted_applications} accepted` },
    { icon: Wallet, label: "Revenue", value: inr(stats.revenue), sub: `${stats.paid_transactions} paid` },
  ];

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-primary" />
              <h1 className="font-heading text-3xl font-extrabold tracking-tight">Command Centre</h1>
            </div>
            <p className="mt-2 text-muted-foreground">Live telemetry across institutes, professionals and admissions.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="admin-add-institute-btn" onClick={() => setInstOpen(true)}><Plus className="mr-2 h-4 w-4" /> Add Institute</Button>
            <Button variant="outline" data-testid="admin-bulk-import-btn" onClick={() => setBulkOpen(true)}><Upload className="mr-2 h-4 w-4" /> Bulk Import</Button>
            <Button data-testid="admin-add-course-btn" onClick={() => setCourseOpen(true)}><Plus className="mr-2 h-4 w-4" /> Add Course</Button>
          </div>
        </div>

        {/* Add institute dialog */}
        <Dialog open={instOpen} onOpenChange={setInstOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="admin-institute-dialog">
            <DialogHeader><DialogTitle>Add an institute</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Institute name</Label><Input data-testid="inst-name" value={instForm.name} onChange={(e) => setI("name", e.target.value)} /></div>
              <div><Label>City</Label><Input data-testid="inst-city" value={instForm.city} onChange={(e) => setI("city", e.target.value)} /></div>
              <div><Label>State</Label><Input data-testid="inst-state" value={instForm.state} onChange={(e) => setI("state", e.target.value)} /></div>
              <div><Label>Accreditation</Label><Input data-testid="inst-accreditation" value={instForm.accreditation} onChange={(e) => setI("accreditation", e.target.value)} placeholder="NMC · NABH" /></div>
              <div><Label>Established</Label><Input data-testid="inst-established" value={instForm.established} onChange={(e) => setI("established", e.target.value)} placeholder="2005" /></div>
              <div className="sm:col-span-2"><Label>Website</Label><Input data-testid="inst-website" value={instForm.website} onChange={(e) => setI("website", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Cover image URL</Label><Input data-testid="inst-cover" value={instForm.cover_url} onChange={(e) => setI("cover_url", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="inst-description" value={instForm.description} onChange={(e) => setI("description", e.target.value)} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInstOpen(false)}>Cancel</Button>
              <Button data-testid="save-institute-btn" onClick={saveInstitute} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add institute"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add course on behalf dialog */}
        <Dialog open={courseOpen} onOpenChange={setCourseOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="admin-course-dialog">
            <DialogHeader><DialogTitle>Add a course on behalf of an institute</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Institute</Label>
                <Select value={courseForm.institute_id} onValueChange={(v) => setC("institute_id", v)}>
                  <SelectTrigger data-testid="course-institute"><SelectValue placeholder="Select institute" /></SelectTrigger>
                  <SelectContent>{institutes.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Course title</Label><Input data-testid="ac-title" value={courseForm.title} onChange={(e) => setC("title", e.target.value)} /></div>
              <div><Label>Specialty</Label><Input data-testid="ac-specialty" value={courseForm.specialty} onChange={(e) => setC("specialty", e.target.value)} placeholder="Emergency Medicine" /></div>
              <div><Label>Format</Label>
                <Select value={courseForm.format} onValueChange={(v) => setC("format", v)}>
                  <SelectTrigger data-testid="ac-format"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Offline", "Hybrid", "Online"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>City</Label><Input data-testid="ac-city" value={courseForm.city} onChange={(e) => setC("city", e.target.value)} /></div>
              <div><Label>State</Label><Input data-testid="ac-state" value={courseForm.state} onChange={(e) => setC("state", e.target.value)} /></div>
              <div><Label>Duration</Label><Input data-testid="ac-duration" value={courseForm.duration} onChange={(e) => setC("duration", e.target.value)} placeholder="12 months" /></div>
              <div><Label>Seats</Label><Input data-testid="ac-seats" type="number" value={courseForm.seats} onChange={(e) => setC("seats", e.target.value)} /></div>
              <div><Label>Course fee (₹)</Label><Input data-testid="ac-fee" type="number" value={courseForm.fee} onChange={(e) => setC("fee", e.target.value)} /></div>
              <div><Label>Application fee (₹)</Label><Input data-testid="ac-appfee" type="number" value={courseForm.application_fee} onChange={(e) => setC("application_fee", e.target.value)} /></div>
              <div><Label>Deadline</Label><Input data-testid="ac-deadline" value={courseForm.application_deadline} onChange={(e) => setC("application_deadline", e.target.value)} placeholder="2026-08-01" /></div>
              <div className="sm:col-span-2"><Label>Image URL</Label><Input data-testid="ac-image" value={courseForm.image_url} onChange={(e) => setC("image_url", e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Eligibility (comma separated)</Label><Input data-testid="ac-eligibility" value={courseForm.eligibility} onChange={(e) => setC("eligibility", e.target.value)} placeholder="MBBS, MD" /></div>
              <div className="sm:col-span-2"><Label>Tags (comma separated)</Label><Input data-testid="ac-tags" value={courseForm.tags} onChange={(e) => setC("tags", e.target.value)} placeholder="Trauma, Critical Care" /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="ac-description" value={courseForm.description} onChange={(e) => setC("description", e.target.value)} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCourseOpen(false)}>Cancel</Button>
              <Button data-testid="save-ac-btn" onClick={saveCourse} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish course"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} institutes={institutes} onDone={load} />

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {cards.map((c) => (
            <div key={c.label} data-testid={`admin-stat-${c.label.toLowerCase()}`} className="rounded-2xl border border-border bg-card p-5">
              <c.icon className="h-5 w-5 text-primary" />
              <p className="mt-3 font-mono-nums text-2xl font-extrabold">{c.value}</p>
              <p className="text-sm text-muted-foreground">{c.label}</p>
              {c.sub && <p className="mt-1 text-xs text-accent">{c.sub}</p>}
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-heading font-bold">Courses by State</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.by_state}>
                  <XAxis dataKey="state" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>{stats.by_state.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-heading font-bold">Courses by Specialty</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.by_specialty} layout="vertical" margin={{ left: 40 }}>
                  <XAxis type="number" allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="specialty" width={120} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>{stats.by_specialty.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <Tabs defaultValue="activity" className="mt-10">
          <TabsList>
            <TabsTrigger value="activity" data-testid="tab-activity">Live Activity Feed</TabsTrigger>
            <TabsTrigger value="institutes" data-testid="tab-inst-queue">Institute Directory</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims">Claim Requests{claims.length ? ` (${claims.length})` : ""}</TabsTrigger>
          </TabsList>
          <TabsContent value="activity" className="pt-4">
            <div className="overflow-x-auto rounded-2xl border border-border bg-card">
              <Table>
                <TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Course</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {activity.map((a) => (
                    <TableRow key={a.id} data-testid={`activity-row-${a.id}`}>
                      <TableCell className="font-medium">{a.applicant?.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.course?.title}</TableCell>
                      <TableCell><StatusBadge status={a.payment_status} /></TableCell>
                      <TableCell><StatusBadge status={a.status} /></TableCell>
                    </TableRow>
                  ))}
                  {activity.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No activity yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="institutes" className="pt-4">
            <div className="overflow-x-auto rounded-2xl border border-border bg-card">
              <Table>
                <TableHeader><TableRow><TableHead>Institute</TableHead><TableHead>City</TableHead><TableHead>Courses</TableHead><TableHead>Applications</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {institutes.map((i) => (
                    <TableRow key={i.id} data-testid={`admin-inst-${i.id}`}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{i.city}, {i.state}</TableCell>
                      <TableCell className="font-mono-nums">{i.course_count}</TableCell>
                      <TableCell className="font-mono-nums">{i.application_count}</TableCell>
                      <TableCell>
                        {i.verified ? (
                          <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary"><BadgeCheck className="h-4 w-4" /> Verified</span>
                        ) : (
                          <Button size="sm" variant="outline" data-testid={`verify-btn-${i.id}`} onClick={() => verify(i.id)}>Verify</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="claims" className="pt-4">
            <div className="overflow-x-auto rounded-2xl border border-border bg-card">
              <Table>
                <TableHeader><TableRow><TableHead>Institute</TableHead><TableHead>Requested by</TableHead><TableHead>Courses</TableHead><TableHead className="text-right">Decision</TableHead></TableRow></TableHeader>
                <TableBody>
                  {claims.map((c) => (
                    <TableRow key={c.id} data-testid={`claim-row-${c.id}`}>
                      <TableCell className="font-medium">{c.institute_name}</TableCell>
                      <TableCell><div className="text-sm">{c.user_name}</div><div className="text-xs text-muted-foreground">{c.user_email}</div></TableCell>
                      <TableCell className="font-mono-nums">{c.course_count}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" data-testid={`approve-claim-${c.id}`} onClick={() => approveClaim(c.id)}><Check className="mr-1 h-4 w-4" /> Approve</Button>
                          <Button size="sm" variant="ghost" className="text-destructive" data-testid={`reject-claim-${c.id}`} onClick={() => rejectClaim(c.id)}><X className="mr-1 h-4 w-4" /> Reject</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {claims.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No pending claim requests.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
